import { useEffect } from 'react';
import { useV2Query } from 'utils';
import { ApolloError, QueryResult } from '@apollo/client';
import { GET_PERSONAL_BUSINESS_UNCLASSIFIED_DETAILS } from '../../../queries';
import { PersonalBusinessDetails } from '../types';

interface UsePersonalBusinessDataProps {
  clientId: string;
  startDate: Date;
  endDate: Date;
  pollInterval?: number;
  disableAutoPagination?: boolean; // For testing purposes
}

interface UsePersonalBusinessDataReturn {
  personalBusinessData: PersonalBusinessDetails | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  updateQuery: QueryResult<PersonalBusinessDetails>['updateQuery'];
  fetchMore: QueryResult<PersonalBusinessDetails>['fetchMore'];
  refetch: QueryResult<PersonalBusinessDetails>['refetch'];
}

export const usePersonalBusinessData = ({
  clientId,
  startDate,
  endDate,
  pollInterval = 0,
  disableAutoPagination = false,
}: UsePersonalBusinessDataProps): UsePersonalBusinessDataReturn => {
  const {
    data: personalBusinessData,
    loading,
    error,
    updateQuery,
    fetchMore,
    refetch,
  } = useV2Query<PersonalBusinessDetails>(GET_PERSONAL_BUSINESS_UNCLASSIFIED_DETAILS, {
    variables: {
      clientId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
    pollInterval,
  });

  const personalBusinessDetails =
    personalBusinessData?.personalBusinessClassificationDetailsByClient;

  useEffect(() => {
    if (loading || disableAutoPagination) return;

    if (
      personalBusinessDetails?.dataEntries?.pageInfo?.hasNextPage &&
      personalBusinessDetails?.dataEntries?.pageInfo?.total !==
        personalBusinessDetails?.dataEntries?.dataEntries?.length &&
      personalBusinessDetails?.dataEntries?.pageInfo?.endCursor
    ) {
      fetchMore({
        variables: {
          after: personalBusinessDetails?.dataEntries?.pageInfo?.endCursor,
        },
      });
    }
  }, [loading, fetchMore, personalBusinessDetails, disableAutoPagination]);

  return {
    personalBusinessData,
    loading,
    error,
    updateQuery,
    fetchMore,
    refetch,
  };
};
